import React, { useEffect, useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import { fetchQuizStats } from "@/lib/api";

const LABELS = {
  archetype: "Result archetypes",
  need: "What they want more of",
  experience: "Preferred experience",
  sensory: "Sensory direction",
  audience: "Shopping for",
  category: "Category fit",
};

function Bars({ data }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = entries.length ? Math.max(...entries.map((e) => e[1])) : 1;
  if (!entries.length) return <p className="text-base text-wood-500">No data yet.</p>;
  return (
    <div className="flex flex-col gap-3">
      {entries.map(([k, v]) => (
        <div key={k}>
          <div className="flex justify-between text-base text-wood-700">
            <span className="capitalize">{String(k).replace(/-/g, " ")}</span>
            <span className="font-semibold">{v}</span>
          </div>
          <div className="mt-1 h-2.5 w-full overflow-hidden rounded-full bg-sand">
            <div className="h-full rounded-full bg-honey" style={{ width: `${(v / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Insights() {
  const [key, setKey] = useState(sessionStorage.getItem("wfp_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { document.title = "Quiz Insights · Washington Family Provisions"; }, []);

  const load = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetchQuizStats(key);
      setData(res);
      setAuthed(true);
      sessionStorage.setItem("wfp_admin_key", key);
    } catch {
      setError("Incorrect passcode.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (key && !authed) load(); /* eslint-disable-next-line */ }, []);

  if (!authed) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-5 py-20">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-honey/15 text-honey-dark"><Lock size={24} /></div>
        <h1 className="mt-6 font-serif text-4xl text-wood-900">Quiz Insights</h1>
        <p className="mt-2 text-base text-wood-700">Enter your passcode to view anonymous quiz analytics.</p>
        <form onSubmit={load} className="mt-6 flex flex-col gap-3">
          <input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Passcode" data-testid="insights-passcode" className="rounded-full border border-wood-900/20 bg-white px-6 py-4 text-base outline-none focus:border-honey-dark" />
          {error && <p className="text-base text-rose">{error}</p>}
          <button type="submit" disabled={loading} data-testid="insights-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-wood-900 px-8 py-4 text-base font-medium text-cream hover:bg-honey-dark disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={18} /> : "View insights"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-5 py-16 md:px-8" data-testid="insights-dashboard">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-honey-dark">Private dashboard</p>
          <h1 className="mt-2 font-serif text-5xl text-wood-900">Quiz Insights</h1>
        </div>
        <div className="text-right">
          <p className="font-serif text-5xl text-wood-900">{data?.total ?? 0}</p>
          <p className="text-base text-wood-500">quizzes completed</p>
        </div>
      </div>
      <div className="mt-12 grid gap-8 md:grid-cols-2">
        {Object.keys(LABELS).map((f) => (
          <div key={f} className="rounded-3xl bg-white p-6 shadow-[0_6px_30px_rgba(62,49,40,0.05)]">
            <h2 className="mb-4 font-serif text-2xl text-wood-900">{LABELS[f]}</h2>
            <Bars data={data?.stats?.[f]} />
          </div>
        ))}
      </div>
    </div>
  );
}
